import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Panel, PanelHeader, PanelHeaderBack, Group, Div, Button, Spinner,
  FormItem, Input, Text, Headline, ModalRoot,
  ModalPageHeader, IconButton, Snackbar, Select, Card, Checkbox,
} from '@vkontakte/vkui';
import { Icon24Add, Icon24Delete, Icon24Write, Icon24Cancel, Icon24Phone, Icon24Link } from '@vkontakte/icons';
import { api } from '../../api/client';
import { useWeddingStore } from '../../stores/wedding';
import type { Vendor, VendorStatus, ExpenseCategory } from '../../types';
import { DragHandle } from '../../ui/DragHandle';
import { ResizableModalPage, sheetId } from '../../ui/ResizableModalPage';

const STATUS_LABEL: Record<VendorStatus, string> = {
  pending: 'Рассматривается',
  reserved: 'Забронирован',
  rejected: 'Отклонён',
};

const STATUS_COLOR: Record<VendorStatus, string> = {
  pending: 'var(--vkui--color_text_secondary)',
  reserved: 'var(--vkui--color_text_positive)',
  rejected: 'var(--vkui--color_text_negative)',
};

type FormState = {
  name: string;
  note: string;
  phone: string;
  email: string;
  website: string;
  address: string;
  amount: string;
  status: VendorStatus;
  category_id: string;
  in_budget: boolean;
};

const emptyForm: FormState = {
  name: '', note: '', phone: '', email: '', website: '',
  address: '', amount: '', status: 'pending', category_id: '',
  in_budget: false,
};

function fmt(n: number | null | undefined): string {
  if (n == null) return '—';
  return `${Number(n).toLocaleString('ru-RU')} ₽`;
}

export default function VendorsPage() {
  const navigate = useNavigate();
  const { currentWedding, weddingsLoaded } = useWeddingStore();

  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [categories, setCategories] = useState<ExpenseCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState<'form' | null>(null);
  const [editing, setEditing] = useState<Vendor | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [snack, setSnack] = useState<string | null>(null);
  const [openToken, setOpenToken] = useState(0);
  const [confirmDelete, setConfirmDelete] = useState<Vendor | null>(null);
  const [filter, setFilter] = useState<VendorStatus | ''>('');
  // mode_vendors: alphabet | category
  const [sortMode, setSortMode] = useState<'alphabet' | 'category'>(
    () => (localStorage.getItem('colibri_mode_vendors') as 'alphabet' | 'category') || 'category',
  );

  function setSort(m: 'alphabet' | 'category') {
    setSortMode(m);
    localStorage.setItem('colibri_mode_vendors', m);
  }

  const loadAll = useCallback(async () => {
    if (!currentWedding) return;
    setLoading(true);
    try {
      const [vs, cats] = await Promise.all([
        api.get<Vendor[]>(`weddings/${currentWedding.id}/vendors`),
        api.get<ExpenseCategory[]>(`weddings/${currentWedding.id}/expenses/categories`),
      ]);
      setVendors(vs);
      setCategories(cats);
    } catch {
      setSnack('Ошибка загрузки подрядчиков');
    } finally {
      setLoading(false);
    }
  }, [currentWedding]);

  useEffect(() => {
    if (!currentWedding) {
      if (weddingsLoaded) navigate('/');
      return;
    }
    void loadAll();
  }, [currentWedding, weddingsLoaded, loadAll, navigate]);

  const catName = useMemo(() => {
    const m = new Map<string, string>();
    for (const c of categories) m.set(c.id, c.name);
    return m;
  }, [categories]);

  const filtered = useMemo(() => {
    if (!filter) return vendors;
    return vendors.filter((v) => v.status === filter);
  }, [vendors, filter]);

  const grouped = useMemo(() => {
    const items = [...filtered];
    if (sortMode === 'alphabet') {
      items.sort((a, b) => a.name.localeCompare(b.name, 'ru'));
      return [{ key: 'all', title: 'По алфавиту', status: undefined as VendorStatus | undefined, items }];
    }
    // category (default)
    const order: VendorStatus[] = ['pending', 'reserved', 'rejected'];
    return order.map((st) => ({
      key: st as string,
      title: STATUS_LABEL[st],
      status: st as VendorStatus | undefined,
      items: items
        .filter((v) => v.status === st)
        .sort((a, b) => a.name.localeCompare(b.name, 'ru')),
    })).filter((g) => g.items.length > 0);
  }, [filtered, sortMode]);

  const stats = useMemo(() => {
    const by = (s: VendorStatus) => vendors.filter((v) => v.status === s).length;
    return { pending: by('pending'), reserved: by('reserved'), rejected: by('rejected') };
  }, [vendors]);

  function openCreate() {
    setEditing(null);
    setForm(emptyForm);
    setOpenToken((t) => t + 1);
    setModal('form');
  }

  function openEdit(v: Vendor) {
    setEditing(v);
    setForm({
      name: v.name,
      note: v.note || '',
      phone: v.phone || '',
      email: v.email || '',
      website: v.website || '',
      address: v.address || '',
      amount: v.amount != null ? String(v.amount) : '',
      status: v.status as VendorStatus,
      category_id: v.category_id || '',
      in_budget: !!v.expense_id,
    });
    setOpenToken((t) => t + 1);
    setModal('form');
  }

  async function handleSave() {
    if (!currentWedding || !form.name.trim()) return;
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        note: form.note.trim() || undefined,
        phone: form.phone.trim() || undefined,
        email: form.email.trim() || undefined,
        website: form.website.trim() || undefined,
        address: form.address.trim() || undefined,
        amount: form.amount ? Number(form.amount) : undefined,
        status: form.status,
        category_id: form.category_id || undefined,
        in_budget: form.in_budget,
      };
      if (editing) {
        await api.patch(`weddings/${currentWedding.id}/vendors/${editing.id}`, payload);
        setSnack('Подрядчик обновлён');
      } else {
        await api.post(`weddings/${currentWedding.id}/vendors`, payload);
        setSnack('Подрядчик добавлен');
      }
      setOpenToken((t) => t + 1);
      setModal(null);
      await loadAll();
    } catch {
      setSnack('Ошибка сохранения');
    } finally {
      setSaving(false);
    }
  }

  async function setStatus(v: Vendor, status: VendorStatus) {
    if (!currentWedding) return;
    try {
      await api.patch(`weddings/${currentWedding.id}/vendors/${v.id}`, { status });
      await loadAll();
    } catch {
      setSnack('Не удалось сменить статус');
    }
  }

  return (
    <Panel>
      <PanelHeader before={<PanelHeaderBack onClick={() => navigate('/')} />}>
        Подрядчики
      </PanelHeader>

      <Group>
        <Div>
          <Button size="l" mode="primary" stretched before={<Icon24Add />} onClick={openCreate}>
            Добавить подрядчика
          </Button>
        </Div>
        <Div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', paddingTop: 0 }}>
          <Button size="s" mode={filter === '' ? 'primary' : 'secondary'} onClick={() => setFilter('')}>
            Все ({vendors.length})
          </Button>
          <Button size="s" mode={filter === 'pending' ? 'primary' : 'secondary'} onClick={() => setFilter(filter === 'pending' ? '' : 'pending')}>
            Рассм. ({stats.pending})
          </Button>
          <Button size="s" mode={filter === 'reserved' ? 'primary' : 'secondary'} onClick={() => setFilter(filter === 'reserved' ? '' : 'reserved')}>
            Бронь ({stats.reserved})
          </Button>
          <Button size="s" mode={filter === 'rejected' ? 'primary' : 'secondary'} onClick={() => setFilter(filter === 'rejected' ? '' : 'rejected')}>
            Отказ ({stats.rejected})
          </Button>
        </Div>
        <Div style={{ display: 'flex', gap: 6, paddingTop: 0 }}>
          <Button size="s" mode={sortMode === 'alphabet' ? 'primary' : 'secondary'} onClick={() => setSort('alphabet')}>
            По алфавиту
          </Button>
          <Button size="s" mode={sortMode === 'category' ? 'primary' : 'secondary'} onClick={() => setSort('category')}>
            По категориям
          </Button>
        </Div>
      </Group>

      {loading ? (
        <Group>
          <Div style={{ textAlign: 'center', padding: 40 }}><Spinner size="l" /></Div>
        </Group>
      ) : vendors.length === 0 ? (
        <Group>
          <Div style={{ textAlign: 'center', padding: 32 }}>
            <Headline level="2">Нет подрядчиков</Headline>
            <Text style={{ marginTop: 8, color: 'var(--vkui--color_text_secondary)' }}>
              Фотограф, ведущий, декоратор…
            </Text>
          </Div>
        </Group>
      ) : (
        grouped.map(({ key, title, status, items }) =>
          items.length === 0 ? null : (
            <Group key={key} header={<Text style={{ color: status ? STATUS_COLOR[status] : 'var(--vkui--color_text_secondary)' }}>{title} ({items.length})</Text>}>
              {items.map((v) => (
                <Card key={v.id} style={{ margin: '8px 12px', padding: 12 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <Headline level="2">{v.name}</Headline>
                      <Text style={{ color: 'var(--vkui--color_text_secondary)', marginTop: 4 }}>
                        {fmt(v.amount)}
                        {v.category_id ? ` · ${catName.get(v.category_id) || ''}` : ''}
                        {v.expense_id ? ' · в бюджете' : ' · не в бюджете'}
                      </Text>
                      {v.phone && (
                        <Text style={{ marginTop: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
                          <Icon24Phone width={14} height={14} /> {v.phone}
                        </Text>
                      )}
                      {v.email && <Text style={{ marginTop: 2 }}>{v.email}</Text>}
                      {v.website && (
                        <Text style={{ marginTop: 2, display: 'flex', alignItems: 'center', gap: 4 }}>
                          <Icon24Link width={14} height={14} />
                          <a href={v.website} target="_blank" rel="noreferrer">{v.website}</a>
                        </Text>
                      )}
                      {v.address && (
                        <Text style={{ marginTop: 2, color: 'var(--vkui--color_text_secondary)' }}>📍 {v.address}</Text>
                      )}
                      {v.note && <Text style={{ marginTop: 4 }}>{v.note}</Text>}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      <IconButton label="Изменить" onClick={() => openEdit(v)}>
                        <Icon24Write />
                      </IconButton>
                      <IconButton label="Удалить" onClick={() => setConfirmDelete(v)}>
                        <Icon24Delete />
                      </IconButton>
                    </div>
                  </div>
                  <Div style={{ display: 'flex', gap: 6, padding: '10px 0 0' }}>
                    {(['pending', 'reserved', 'rejected'] as VendorStatus[]).map((s) => (
                      <Button
                        key={s}
                        size="s"
                        mode={v.status === s ? 'primary' : 'secondary'}
                        onClick={() => v.status !== s && void setStatus(v, s)}
                      >
                        {s === 'pending' ? 'Рассм.' : s === 'reserved' ? 'Бронь' : 'Отказ'}
                      </Button>
                    ))}
                  </Div>
                </Card>
              ))}
            </Group>
          ),
        )
      )}

      <ModalRoot activeModal={modal === 'form' ? sheetId('vendor-form', openToken) : null} onClose={() => setModal(null)}>
        <ResizableModalPage openToken={openToken} id="vendor-form" onClose={() => setModal(null)} settlingHeight={80}>
          <ModalPageHeader>
            {editing ? 'Редактировать' : 'Новый подрядчик'}
          </ModalPageHeader>
        <DragHandle />
          <Group>
            <FormItem top="Название *">
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Студия «Ромашка»" />
            </FormItem>
            <FormItem top="Статус">
              <Select
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value as VendorStatus })}
                options={[
                  { label: STATUS_LABEL.pending, value: 'pending' },
                  { label: STATUS_LABEL.reserved, value: 'reserved' },
                  { label: STATUS_LABEL.rejected, value: 'rejected' },
                ]}
              />
            </FormItem>
            <FormItem top="Стоимость">
              <Input
                type="number"
                inputMode="decimal"
                value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                placeholder="50000"
              />
            </FormItem>
            <FormItem>
              <Checkbox
                checked={form.in_budget}
                onChange={(e) => setForm({ ...form, in_budget: e.target.checked })}
              >
                В бюджет
              </Checkbox>
              <Text style={{ marginTop: 4, color: 'var(--vkui--color_text_secondary)', fontSize: 12 }}>
                Добавить подрядчика как расход
              </Text>
            </FormItem>
            <FormItem top="Категория (бюджет)">
              <Select
                value={form.category_id}
                onChange={(e) => setForm({ ...form, category_id: e.target.value })}
                options={[
                  { label: 'Без категории', value: '' },
                  ...categories.map((c) => ({ label: c.name, value: c.id })),
                ]}
              />
            </FormItem>
            <FormItem top="Телефон">
              <Input type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+7…" />
            </FormItem>
            <FormItem top="Email">
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </FormItem>
            <FormItem top="Сайт">
              <Input value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} placeholder="https://" />
            </FormItem>
            <FormItem top="Адрес">
              <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
            </FormItem>
            <FormItem top="Заметка">
              <Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
            </FormItem>
            <Div>
              <Button size="l" mode="primary" stretched onClick={handleSave} disabled={saving || !form.name.trim()}>
                {saving ? 'Сохранение…' : 'Сохранить'}
              </Button>
              <Button size="l" mode="secondary" stretched style={{ marginTop: 8 }} onClick={() => setModal(null)}>
                Отмена
              </Button>
            </Div>
          </Group>
        </ResizableModalPage>
      </ModalRoot>

      {confirmDelete && (
        <Div style={{ position: 'fixed', inset: 0, zIndex: 100, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Card style={{ padding: 20, width: 280 }}>
            <Headline level="2">Удалить «{confirmDelete.name}»?</Headline>
            <Text style={{ marginTop: 8, fontSize: 14, lineHeight: 1.45, color: 'var(--vkui--color_text_secondary)' }}>
              Вместе с подрядчиком будут удалены его <strong>расход</strong> и все
              <strong> платежи</strong> в бюджете. Действие необратимо.
            </Text>
            <Text style={{ marginTop: 10, fontSize: 14, fontWeight: 600 }}>
              Вы уверены?
            </Text>
            <Text style={{ marginTop: 8, color: 'var(--vkui--color_text_secondary)', fontSize: 13 }}>
              Расход в бюджете сохранится.
            </Text>
            <Button
              mode="primary"
              stretched
              style={{ marginTop: 16 }}
              onClick={async () => {
                try {
                  await api.delete(`weddings/${currentWedding!.id}/vendors/${confirmDelete.id}`);
                  await loadAll();
                  setSnack('Подрядчик удалён');
                } catch {
                  setSnack('Ошибка удаления');
                }
                setOpenToken((t) => t + 1);
                setConfirmDelete(null);
              }}
            >
              Удалить
            </Button>
            <Button mode="secondary" stretched style={{ marginTop: 8 }} onClick={() => setConfirmDelete(null)}>
              Отмена
            </Button>
          </Card>
        </Div>
      )}

      {snack && (
        <Snackbar onClose={() => setSnack(null)} onClosed={() => setSnack(null)} before={<Icon24Cancel />}>
          {snack}
        </Snackbar>
      )}
      <Div style={{ height: 24 }} />
    </Panel>
  );
}
